import { t } from 'i18next';
import { v4 as uuidv4 } from 'uuid';

import {
    readChunkedJsonCookie,
    writeChunkedJsonCookie
} from 'app/modules/wallforming/components/cookies/cookieStorage';
import { DEFAULT_INPUT_PROFILE_ID } from 'app/modules/wallforming/components/input-binding/registry/inputPresetsRegistry';
import { InputCategory } from 'app/modules/wallforming/components/input-binding/types/inputCategory';
import { InputContext } from 'app/modules/wallforming/components/input-binding/types/inputContext';
import { InputPresetId } from 'app/modules/wallforming/components/input-binding/types/inputPresetId';
import {
    deserializeInputBindings,
    findBindingIssue,
    getPresetSlots,
    resolveInputBindings,
    serializeInputBindings,
    withoutChord,
    withProfileSlot
} from 'app/modules/wallforming/components/input-binding/utils/inputBindingUtils';
import { toastNotifications } from 'shared/utils/toastNotifications';

import type { InputProfile } from 'app/modules/wallforming/components/input-binding/types/inputProfile';
import type { PendingInputBindingEdit } from 'app/modules/wallforming/components/input-binding/types/pendingInputBindingEdit';
import type { InputBindingSliceState } from 'app/modules/wallforming/components/state-management/placement/states/inputBindingSliceState';
import type { PlacementState } from 'app/modules/wallforming/components/state-management/placement/types';
import type { StateCreator } from 'zustand';

const INPUT_BINDING_COOKIE = 'wallforming-input-bindings';

type SetState = (partial: Partial<PlacementState>) => void;

export const selectActiveInputProfile = (state: PlacementState): InputProfile =>
    state.inputProfiles.find((profile) => profile.id === state.activeInputProfileId)!;

function applyProfiles(set: SetState, profiles: readonly InputProfile[], activeId: string): void {
    set({
        inputProfiles: profiles,
        activeInputProfileId: activeId,
        inputBindings: resolveInputBindings(profiles.find((profile) => profile.id === activeId)!)
    });
    if (!writeChunkedJsonCookie(INPUT_BINDING_COOKIE, serializeInputBindings(profiles, activeId))) {
        toastNotifications.warning({ title: t('warning'), message: t('warningInputBindingsNotSaved') });
    }
}

/** Single write path: keeps the derived bindings and the cookie in sync with the profiles. */
function commitProfiles(set: SetState, profiles: readonly InputProfile[], activeId: string): void {
    applyProfiles(set, profiles, activeId);
}

function commitActiveProfile(set: SetState, state: PlacementState, profile: InputProfile): void {
    commitProfiles(
        set,
        state.inputProfiles.map((current) => (current.id === profile.id ? profile : current)),
        profile.id
    );
}

function createProfile(name: string, source?: InputProfile): InputProfile {
    return {
        id: uuidv4(),
        name,
        basedOn: source?.basedOn ?? InputPresetId.Empty,
        overrides: source?.overrides ?? {}
    };
}

const info = (message: string) => toastNotifications.information({ title: t('info'), message });

export const createInputBindingSlice: StateCreator<PlacementState, [], [], InputBindingSliceState> = (set, get) => {
    const { profiles, activeId } = deserializeInputBindings(readChunkedJsonCookie(INPUT_BINDING_COOKIE));

    return {
        isInputBindingPanelOpen: false,
        setIsInputBindingPanelOpen: (open) =>
            set({ isInputBindingPanelOpen: open, inputCaptureTarget: null, inputBindingDialog: null }),
        selectedInputCategory: InputCategory.General,
        setSelectedInputCategory: (category) => set({ selectedInputCategory: category }),
        inputContextFilter: InputContext.Placement,
        setInputContextFilter: (context) => set({ inputContextFilter: context }),

        inputProfiles: profiles,
        activeInputProfileId: activeId,
        inputBindings: resolveInputBindings(profiles.find((profile) => profile.id === activeId)!),
        setActiveInputProfile: (id) => {
            if (get().inputProfiles.some((profile) => profile.id === id)) {
                set({ inputCaptureTarget: null, inputBindingDialog: null });
                commitProfiles(set, get().inputProfiles, id);
            }
        },
        addInputProfile: (name, sourceId, pendingBinding?: PendingInputBindingEdit) => {
            const trimmed = name.trim();
            if (trimmed) {
                let profile = createProfile(
                    trimmed,
                    get().inputProfiles.find((current) => current.id === sourceId)
                );
                if (pendingBinding?.chord === null) {
                    profile = withProfileSlot(profile, pendingBinding.actionId, pendingBinding.slot, null);
                }
                commitProfiles(set, [...get().inputProfiles, profile], profile.id);
                if (pendingBinding) {
                    if (pendingBinding.chord === undefined) {
                        set({ inputCaptureTarget: { actionId: pendingBinding.actionId, slot: pendingBinding.slot } });
                    } else if (pendingBinding.chord !== null) {
                        get().assignInputBinding(pendingBinding.actionId, pendingBinding.slot, pendingBinding.chord);
                    }
                }
            }
        },
        renameInputProfile: (id, name) => {
            const trimmed = name.trim();
            const state = get();
            const profile = state.inputProfiles.find((current) => current.id === id);
            if (trimmed && profile && !profile.readOnly) {
                commitProfiles(
                    set,
                    state.inputProfiles.map((current) => (current.id === id ? { ...current, name: trimmed } : current)),
                    state.activeInputProfileId
                );
            }
        },
        inputProfileEditorRequest: null,
        clearInputProfileEditorRequest: () => set({ inputProfileEditorRequest: null }),
        updateInputProfileEditorName: (name) => {
            const request = get().inputProfileEditorRequest;
            if (request) {
                set({ inputProfileEditorRequest: { ...request, name } });
            }
        },
        updateInputProfileEditorSource: (sourceId) => {
            const request = get().inputProfileEditorRequest;
            if (request) {
                set({ inputProfileEditorRequest: { ...request, sourceId } });
            }
        },
        removeInputProfile: (id) => {
            const state = get();
            if (state.inputProfiles.some((profile) => profile.id === id && !profile.builtIn)) {
                commitProfiles(
                    set,
                    state.inputProfiles.filter((profile) => profile.id !== id),
                    state.activeInputProfileId === id ? DEFAULT_INPUT_PROFILE_ID : state.activeInputProfileId
                );
            }
        },

        inputCaptureTarget: null,
        startInputCapture: (actionId, slot) =>
            set(
                selectActiveInputProfile(get()).readOnly
                    ? { inputBindingDialog: { kind: 'readOnlyProfile', actionId, slot }, inputCaptureTarget: null }
                    : { inputCaptureTarget: { actionId, slot }, inputBindingDialog: null }
            ),
        cancelInputCapture: () => set({ inputCaptureTarget: null }),
        assignInputBinding: (actionId, slot, chord) => {
            const state = get();
            const profile = selectActiveInputProfile(state);
            if (profile.readOnly) {
                set({
                    inputBindingDialog: { kind: 'readOnlyProfile', actionId, slot, chord },
                    inputCaptureTarget: null
                });
                return;
            }

            const issue = chord === null ? null : findBindingIssue(state.inputBindings, actionId, chord, slot);
            switch (issue?.kind) {
                case undefined:
                    set({ inputCaptureTarget: null });
                    commitActiveProfile(set, state, withProfileSlot(profile, actionId, slot, chord));
                    return;
                case 'conflict':
                    set({
                        inputCaptureTarget: null,
                        inputBindingDialog: {
                            kind: 'conflict',
                            actionId,
                            slot,
                            chord: chord!,
                            conflicts: issue.actionIds
                        }
                    });
                    return;
                case 'sameAction':
                    info(t('infoKeyBindingAlreadyAssignedSameAction'));
                    return;
                case 'reserved':
                    info(t('infoKeyBindingReserved', { action: t(issue.reserved.labelKey) }));
                    return;
                case 'notAllowed':
                    return;
                default:
                    issue satisfies never;
            }
        },
        resetInputBindingSlot: (actionId, slot) =>
            get().assignInputBinding(actionId, slot, getPresetSlots(selectActiveInputProfile(get()), actionId)[slot]),
        resetInputProfile: () => {
            const state = get();
            const profile = selectActiveInputProfile(state);
            if (!profile.readOnly) {
                commitActiveProfile(set, state, { ...profile, overrides: {} });
            }
        },

        inputBindingDialog: null,
        closeInputBindingDialog: () => set({ inputBindingDialog: null }),
        confirmInputBindingDialog: () => {
            const state = get();
            const dialog = state.inputBindingDialog;
            const profile = selectActiveInputProfile(state);
            set({ inputBindingDialog: null });

            switch (dialog?.kind) {
                case 'conflict':
                    commitActiveProfile(
                        set,
                        state,
                        withProfileSlot(
                            withoutChord(profile, dialog.conflicts, dialog.chord),
                            dialog.actionId,
                            dialog.slot,
                            dialog.chord
                        )
                    );
                    info(t('infoKeyBindingOverwritten'));
                    return;
                case 'readOnlyProfile': {
                    set({
                        inputProfileEditorRequest: {
                            mode: 'create',
                            sourceId: profile.id,
                            name: t('labelKeyBindingProfileCopyName', { name: profile.name }),
                            pendingBinding: {
                                actionId: dialog.actionId,
                                slot: dialog.slot,
                                ...(dialog.chord === undefined ? {} : { chord: dialog.chord })
                            }
                        }
                    });
                    return;
                }
                case undefined:
                    return;
                default:
                    dialog satisfies never;
            }
        },

        exportInputBindings: () =>
            JSON.stringify(serializeInputBindings(get().inputProfiles, get().activeInputProfileId), null, 2)
    };
};
